
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Typography, Alert, Space, Spin, Result, Divider } from 'antd';
import { CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, LeftOutlined, LoadingOutlined } from '@ant-design/icons';
import { downloadPPIReport } from '../../../services/getPPI';

const { Title, Text, Paragraph } = Typography;

interface NextStepsProps {
  docId: string;
  email?: string | null;
  emailError?: string | null;
  sendEmail: boolean;
  onComplete?: () => void;
  onBack?: () => void;
}

const NextSteps: React.FC<NextStepsProps> = ({ docId, email, emailError, sendEmail, onComplete, onBack }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await downloadPPIReport(docId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PPI_Report_${docId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      {emailError ? (
        <div style={{ textAlign: 'center' }}>
          <WarningOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
          <Title level={3}>Report Generated with an Issue</Title>
          <Paragraph type="secondary" style={{ fontSize: 16 }}>
            The analysis is complete and your report has been saved to your profile.
          </Paragraph>
          <Alert
            message="Email Delivery Failed"
            description={emailError}
            type="warning"
            showIcon
            style={{ textAlign: 'left', marginBottom: 24 }}
          />
        </div>
      ) : sendEmail ? (
        <Result
          status="success"
          title="Report Sent!"
          subTitle={`A PDF of the report has been sent to ${email}`}
        />
      ) : (
        <Result
          status="success"
          title="Report Generated!"
          subTitle="Result completed, you can download a copy of the report below."
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16, marginBottom: 24 }}>
        {isDownloading ? (
          <Button disabled>
            <LoadingOutlined spin /> Downloading...
          </Button>
        ) : (
          <Button onClick={handleDownload}>Download Report</Button>
        )}
      </div>
      <Divider />
      <div style={{ textAlign: 'center', padding: '24px', background: '#f9f9f9', borderRadius: '8px' }}>
        <Paragraph style={{ fontSize: '16px', maxWidth: '800px', margin: '0 auto 24px auto' }}>
          In the full version, you will be given suggestions to improve your report with the option to re-run with new information. These suggestions will be vehicle specific, letting you know where to look for critical issues and what documentation you should be anticipating.
        </Paragraph>
        <Text type="secondary">
          Impressed? {' '}
        </Text>
        <a href="https://cal.com/yousifmemon/demo" target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
                    Schedule a Demo
                  </a>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
        <Link to={`/`}>
          <Button type="primary" size="large">
            Run Another Report
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default NextSteps;
